import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Badge,
  Fab,
} from "@mui/material";
import { ArrowBack, FilterList, Close, Refresh, FilterAlt, Add, Edit } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import EditDelete from "../../../../components/EditDelete";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";
import useInventory from "../../../../hooks/useInventory";
import axios from "../../../../utils/axios";
import DatePicker from "react-multi-date-picker";
import ToolBar from "../../../../components/Toolbar";
import { saveFile } from "../../../../utils/common";

function AdjustmentSection() {
  const {
    getAllStockAdjustments,
    addStockAdjustment,
    updateStockAdjustment,
    exportInventoryReport,
    isLoading,
  } = useInventory();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [isAdding, setIsAdding] = useState(false);
  const [materials, setMaterials] = useState([]);

  // Form State
  const [materialId, setMaterialId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [type, setType] = useState("Opening");
  const [remarks, setRemarks] = useState("");
  const [editingId, setEditingId] = useState(null);

  // Export State
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState("");

  // Filter State
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterDate, setFilterDate] = useState("");
  const [filterMaterialName, setFilterMaterialName] = useState("");

  // Temp/Modal Filter State
  const [tempDate, setTempDate] = useState("");
  const [tempMaterialName, setTempMaterialName] = useState("");

  const fetchAdjustments = async (date = filterDate, material = filterMaterialName) => {
    try {
      const activeFilters = [];
      if (date) {
        activeFilters.push({
          fieldName: "transactionDate",
          fieldValue: date,
          op: "eq",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
      if (material.trim()) {
        activeFilters.push({
          fieldName: "materialName",
          fieldValue: material.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }

      const payload = {
        pagination: { pageNumber: paginationModel.page + 1, pageSize: paginationModel.pageSize },
        filters: activeFilters,
        sortFields: [{ field: "materialTransactionId", sort: "desc" }],
      };
      const res = await getAllStockAdjustments(payload);
      if (res) {
        setRows(res.data || []);
        setRowCount(res.paging?.totalNumberOfRecords || 0);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMaterials = async () => {
    try {
      const res = await axios.post("/material/list", {
        pagination: { pageNumber: 1, pageSize: 1000 },
        filters: [],
        sortFields: [],
      });
      setMaterials(res.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchAdjustments();
  }, [paginationModel, filterDate, filterMaterialName]);

  useEffect(() => {
    if (isAdding) {
      fetchMaterials();
    }
  }, [isAdding]);

  const dispatch = useDispatch();

  const handleEdit = (row) => {
    setMaterialId(row.materialId || "");
    const qty = row.inQuantity > 0 ? row.inQuantity : -row.outQuantity;
    setQuantity(qty.toString());
    setType(
      row.transactionType === "Opening" || row.transactionType === "OPENING"
        ? "Opening"
        : "Adjustment",
    );
    setRemarks(row.remarks || "");
    setEditingId(row.materialTransactionId);
    setIsAdding(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!materialId) {
      dispatch(showErrorSnackbar("Please select a material"));
      return;
    }
    const qtyVal = parseInt(quantity);
    if (isNaN(qtyVal) || qtyVal === 0) {
      dispatch(showErrorSnackbar("Please enter a non-zero quantity"));
      return;
    }

    try {
      const payload = {
        materialId: parseInt(materialId),
        quantity: qtyVal,
        type,
        remarks,
      };
      if (editingId) {
        await updateStockAdjustment(editingId, payload);
        dispatch(showSuccessSnackbar("Stock adjustment updated successfully!"));
      } else {
        await addStockAdjustment(payload);
        dispatch(showSuccessSnackbar("Stock adjustment submitted successfully!"));
      }
      setIsAdding(false);
      setEditingId(null);
      fetchAdjustments();
      // Reset form
      setMaterialId("");
      setQuantity("");
      setType("Opening");
      setRemarks("");
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || "Failed to submit stock adjustment"));
    }
  };

  const handleDownloadFile = async (fileType) => {
    setDownloadType(fileType);
    setIsDownloading(true);
    try {
      const activeFilters = [];
      if (filterDate) {
        activeFilters.push({
          fieldName: "transactionDate",
          fieldValue: filterDate,
          op: "eq",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
      if (filterMaterialName.trim()) {
        activeFilters.push({
          fieldName: "materialName",
          fieldValue: filterMaterialName.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      const payload = {
        filters: activeFilters,
      };

      const resBlob = await exportInventoryReport("opening-stock", fileType, payload);
      const filename = `opening_stock_${new Date().toISOString().slice(0, 10)}.${fileType}`;
      await saveFile(resBlob, filename);
      dispatch(showSuccessSnackbar(`Report exported successfully as ${fileType.toUpperCase()}!`));
    } catch (err) {
      console.error(err);
      dispatch(showErrorSnackbar("Failed to export report"));
    } finally {
      setIsDownloading(false);
      setDownloadType("");
    }
  };

  const handleApplyFilter = () => {
    setFilterDate(tempDate);
    setFilterMaterialName(tempMaterialName);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setIsFilterOpen(false);
  };

  const handleResetFilter = () => {
    setFilterDate("");
    setFilterMaterialName("");
    setTempDate("");
    setTempMaterialName("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setIsFilterOpen(false);
  };

  const handleRefresh = () => {
    fetchAdjustments();
  };

  const activeFiltersCount = (filterDate ? 1 : 0) + (filterMaterialName ? 1 : 0);

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <EditDelete
          rowId={params.row.materialTransactionId}
          editPage={() => handleEdit(params.row)}
          menus={["edit"]}
        />
      ),
    },
    { field: "transactionDate", headerName: "Date", width: 180 },
    { field: "referenceNo", headerName: "Ref No", width: 150 },
    { field: "materialName", headerName: "Material Name", width: 220 },
    { field: "transactionType", headerName: "Type", width: 140 },
    {
      field: "adjustedQuantity",
      headerName: "Adjusted Qty",
      width: 140,
      valueGetter: (value, row) => {
        const inQty = row?.inQuantity || 0;
        const outQty = row?.outQuantity || 0;
        return inQty > 0 ? `+${inQty}` : `-${outQty}`;
      },
    },
    { field: "balanceQuantity", headerName: "New Balance Qty", width: 160, type: "number" },
    { field: "remarks", headerName: "Remarks", width: 250 },
  ];

  if (isAdding) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h5">
            {editingId ? "Edit Stock Adjustment" : "Log Stock Adjustment / Opening Balance"}
          </Typography>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => {
              setIsAdding(false);
              setEditingId(null);
              setMaterialId("");
              setQuantity("");
              setType("Opening");
              setRemarks("");
            }}
          >
            Back to List
          </Button>
        </Box>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={8}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="material-select-label">Select Material</InputLabel>
                <Select
                  labelId="material-select-label"
                  value={materialId}
                  label="Select Material"
                  onChange={(e) => setMaterialId(e.target.value)}
                >
                  {materials.map((m) => (
                    <MenuItem key={m.materialId} value={m.materialId}>
                      {m.materialName} ({m.materialCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="type-select-label">Adjustment Type</InputLabel>
                <Select
                  labelId="type-select-label"
                  value={type}
                  label="Adjustment Type"
                  onChange={(e) => setType(e.target.value)}
                >
                  <MenuItem value="Opening">Opening Stock / Initial Balance</MenuItem>
                  <MenuItem value="Adjustment">Stock Correction / Adjustment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Quantity (positive to add, negative to subtract)"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputProps={{ step: "1" }}
                onWheel={(e) => e.target.blur()}
                required
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Remarks / Reason"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save Adjustment"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h5">Stock Adjustments & Opening Balances</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <ToolBar
            addPage={() => setIsAdding(true)}
            refreshPage={handleRefresh}
            openFilterDialog={() => setIsFilterOpen(true)}
            downloadFile={handleDownloadFile}
            isLoadingDownload={isDownloading}
            downloadFileType={downloadType}
          />
        </Box>
      </Box>
      <Paper elevation={0}>
        <div style={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) =>
              row.materialTransactionId || row.referenceNo + "-" + row.transactionDate
            }
            paginationMode="server"
            rowCount={rowCount}
            pageSizeOptions={[10, 25, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            loading={isLoading}
            disableRowSelectionOnClick
          />
        </div>
      </Paper>

      {/* Advance Filter Dialog */}
      <Dialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6">Advance Filter</Typography>
          <IconButton onClick={() => setIsFilterOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Material Name"
                value={tempMaterialName}
                onChange={(e) => setTempMaterialName(e.target.value)}
                placeholder="Search material name"
              />
            </Grid>
            <Grid item xs={12}>
              <DatePicker
                containerStyle={{ width: "100%" }}
                portal
                zIndex={10000}
                range
                value={tempDate ? tempDate.split(",") : []}
                onChange={(dates) => {
                  if (dates && dates.length > 0) {
                    const formatted = dates.map((d) => d.format("YYYY-MM-DD")).join(",");
                    setTempDate(formatted);
                  } else {
                    setTempDate("");
                  }
                }}
                format="YYYY/MM/DD"
                render={
                  <TextField
                    fullWidth
                    label="Date"
                    variant="outlined"
                    InputLabelProps={{ shrink: !!tempDate }}
                  />
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleResetFilter} variant="outlined" startIcon={<Refresh />}>
            Reset
          </Button>
          <Button
            onClick={handleApplyFilter}
            variant="contained"
            color="primary"
            startIcon={<FilterAlt />}
          >
            filter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdjustmentSection;
